import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="analytics--filter"
export default class extends Controller {
  static targets = ["startDate", "endDate", "partyType", "partyStatus"];
  static values = {
    persistState: Boolean,
  };

  connect() {
    this.loadPersistedState();
    this.updateFilterDisplay();
  }

  // Handle filter changes
  handleFilterChange(event) {
    const target = event.target;

    if (
      target.matches('[data-analytics--filter-target="startDate"]') ||
      target.matches('[data-analytics--filter-target="endDate"]')
    ) {
      this.updateCustomDateRange();
    } else if (target.matches("[data-filter-type]")) {
      this.updateFilter(event);
    }
  }

  // Update custom date range from individual date inputs
  updateCustomDateRange() {
    if (!this.hasStartDateTarget || !this.hasEndDateTarget) {
      return;
    }

    const startDate = this.startDateTarget.value;
    const endDate = this.endDateTarget.value;

    // Only apply filter when both dates are selected
    if (startDate && endDate) {
      if (new Date(startDate) <= new Date(endDate)) {
        // Apply custom date range filter
        const filters = { ...this.getCurrentFilters() };
        filters.start_date = startDate;
        filters.end_date = endDate;
        this.setCurrentFilters(filters);
        this.persistState();
        this.updateFilterDisplay();
      } else {
        this.showError("Start date must be before end date");
      }
    } else if (startDate || endDate) {
      // If only one date is selected, clear the date range filter
      const filters = { ...this.getCurrentFilters() };
      delete filters.start_date;
      delete filters.end_date;
      this.setCurrentFilters(filters);
      this.persistState();
      this.updateFilterDisplay();
    }
  }

  // Update individual filter
  updateFilter(event) {
    const target = event.target;
    const filterType = target.dataset.filterType;
    const filterValue = target.value;

    const filters = { ...this.getCurrentFilters() };

    if (filterValue === "" || filterValue === "all") {
      delete filters[filterType];
    } else {
      filters[filterType] = filterValue;
    }

    this.setCurrentFilters(filters);
    this.persistState();
    this.updateFilterDisplay();
  }

  // Reset all filters to default
  resetFilters(event) {
    if (event) {
      event.preventDefault();
    }

    // Clear all filter parameters and reload page
    const url = new URL(window.location);

    // Clear existing filter parameters
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith("filter_")) {
        url.searchParams.delete(key);
      }
    }

    window.location.href = url.toString();
  }

  // Handle refresh button click
  handleRefreshClick(event) {
    if (event) {
      event.preventDefault();
    }

    const filters = this.getCurrentFilters();
    this.reloadPageWithFilters(filters);
  }

  // Reload page with current filters
  reloadPageWithFilters(filters) {
    const url = new URL(window.location);

    // Clear existing filter parameters
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith("filter_")) {
        url.searchParams.delete(key);
      }
    }

    // Add current filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "all" && value !== "") {
        url.searchParams.set(`filter_${key}`, value);
      }
    });

    window.location.href = url.toString();
  }

  // Update filter display (show active filters count, etc.)
  updateFilterDisplay() {
    const filters = this.getCurrentFilters();
    const activeFilterCount = Object.keys(filters).filter(
      (key) => filters[key] !== "all" && filters[key] !== "",
    ).length;

    // Update active filter indicator
    const indicator = this.element.querySelector("[data-active-filters]");
    if (indicator) {
      indicator.textContent =
        activeFilterCount > 0 ? `${activeFilterCount} active` : "";
      indicator.style.display = activeFilterCount > 0 ? "inline" : "none";
    }

    // Update reset button visibility
    const resetButton = this.element.querySelector(
      '[data-action*="resetFilters"]',
    );
    if (resetButton) {
      resetButton.style.display =
        activeFilterCount > 0 ? "inline-block" : "none";
    }
  }

  // Get current filters
  getCurrentFilters() {
    return this.currentFilters || {};
  }

  // Set current filters
  setCurrentFilters(filters) {
    this.currentFilters = filters;
  }

  // Persist filter state to localStorage
  persistState() {
    if (!this.persistStateValue) return;

    const filters = this.getCurrentFilters();

    try {
      localStorage.setItem("analytics_filters", JSON.stringify(filters));
    } catch (error) {
      console.warn("Failed to persist filter state:", error);
    }
  }

  // Load persisted filter state
  loadPersistedState() {
    if (!this.persistStateValue) return;

    try {
      // Try to load from URL parameters first
      const urlFilters = this.getFiltersFromURL();
      if (Object.keys(urlFilters).length > 0) {
        this.setCurrentFilters(urlFilters);
        this.applyPersistedFilters(urlFilters);
        return;
      }

      // Fall back to localStorage
      const stored = localStorage.getItem("analytics_filters");
      if (stored) {
        const filters = JSON.parse(stored);
        this.setCurrentFilters(filters);
        this.applyPersistedFilters(filters);
      }
    } catch (error) {
      console.warn("Failed to load persisted filter state:", error);
    }
  }

  // Apply persisted filters to form inputs
  applyPersistedFilters(filters) {
    // Set individual date inputs
    if (filters.start_date && this.hasStartDateTarget) {
      this.startDateTarget.value = filters.start_date;
    }
    if (filters.end_date && this.hasEndDateTarget) {
      this.endDateTarget.value = filters.end_date;
    }

    // Set other filters
    Object.keys(filters).forEach((key) => {
      if (key === "start_date" || key === "end_date") return;

      const input = this.element.querySelector(`[data-filter-type="${key}"]`);
      if (input && filters[key]) {
        input.value = filters[key];
      }
    });
  }

  // Get filters from URL parameters
  getFiltersFromURL() {
    const params = new URLSearchParams(window.location.search);
    const filters = {};

    for (const [key, value] of params.entries()) {
      if (key.startsWith("filter_")) {
        filters[key.replace("filter_", "")] = value;
      }
    }

    return filters;
  }

  // Show error message
  showError(message) {
    const errorElement = this.element.querySelector("[data-filter-error]");
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.remove("hidden");

      // Auto-hide after 3 seconds
      setTimeout(() => {
        errorElement.classList.add("hidden");
      }, 3000);
    }
  }
}
